import { useCallback, useMemo } from "react";
import _defaults from "lodash/defaults";

import {
  Button,
  DateInput,
  Form,
  Input,
  PickerInput,
  RadioInput,
  TextInput,
} from "../lib";
import {
  Country,
  getDateFromServerTimestamp,
  getServerTimestampFromDate,
  useCities,
  useConfig,
  User,
  UserIdentityUpdateReq,
  useServerForm,
  useUserIdentityUpdate,
} from "../services";
import { CountryInput } from "./country-input";
import { Callout } from "./callout";

const defaultInitialValues: UserIdentityUpdateReq = {
  cancellation_policy: null,
  city_id: null,
  description: null,
  dob: null,
  gender_id: 1,
  name: null,
  nationality_id: null,
  payment_method: null,
};

type Props = {
  user: User;
  onSuccess?: () => void;
  onVerifyIdentity?: () => void;
};

export function IdentityForm({ user, onSuccess, onVerifyIdentity }: Props) {
  // Data
  const { data: cities } = useCities();
  const { data: config } = useConfig();

  // Vars
  const cityOptions = useMemo(() => {
    return (cities ?? []).map((city) => ({
      label: city.name,
      value: city.id,
    }));
  }, [cities]);

  const genderOptions = useMemo(() => {
    return (config?.genders ?? []).map((gender) => ({
      label: gender.name,
      value: gender.value,
    }));
  }, [config]);

  const initialValues: UserIdentityUpdateReq = useMemo(() => {
    return _defaults(
      {
        name: user.name,
        cancellation_policy: user.cancellation_policy,
        city_id: user.city?.id ?? null,
        description: user.description,
        dob: user.dob ? getServerTimestampFromDate(new Date(user.dob)) : null,
        gender_id: user.gender_id ?? null,
        nationality_id: user.nationality?.id ?? null,
        payment_method: user.payment_method,
      },
      defaultInitialValues
    );
  }, [user]);

  const [updateUserIdentity, isUpdating, error] = useUserIdentityUpdate();
  const { errors, values, setValue } = useServerForm(initialValues, error);

  // Handlers
  const handleChangeNationality = useCallback(
    (country: Country) => {
      setValue("nationality_id", country.id);
    },
    [setValue]
  );

  const handleChangeDob = useCallback(
    (_name: "dob", date: Date | null) => {
      setValue("dob", date ? getServerTimestampFromDate(date) : null);
    },
    [setValue]
  );

  const handleSubmit = useCallback(async () => {
    await updateUserIdentity(values);

    onSuccess?.();
  }, [onSuccess, updateUserIdentity, values]);

  // Render
  if (!config) {
    return null;
  }

  const isIdentityPending = user.identity_status === "pending";

  return (
    <Form>
      {user.identity_required &&
        !user.identity_verified &&
        !isIdentityPending && (
          <Callout
            icon="fingerprint"
            action="Upload ID"
            title="KYC required"
            message={`Identity ${user.identity_status?.toUpperCase()}`}
            onPress={onVerifyIdentity}
            mb="4"
          />
        )}

      <Input label="Nickname" error={errors.name}>
        <TextInput name="name" value={values.name} onChange={setValue} />
      </Input>
      <Input label="City" error={errors.city_id}>
        <PickerInput
          name="city_id"
          value={values.city_id}
          onChange={setValue}
          options={cityOptions}
        />
      </Input>
      <Input label="Nationality" error={errors.nationality_id}>
        <CountryInput
          onChange={handleChangeNationality}
          placeholder="Select country"
          value={values.nationality_id}
        />
      </Input>
      <Input label="Gender" error={errors.gender_id}>
        <RadioInput
          name="gender_id"
          options={genderOptions}
          onChange={setValue}
          value={values.gender_id}
        />
      </Input>

      <Input label="Date of birth" error={errors.dob}>
        <DateInput
          name="dob"
          value={values.dob ? getDateFromServerTimestamp(values.dob) : null}
          onChange={handleChangeDob}
        />
      </Input>

      <Input label="Description" error={errors.description}>
        <TextInput
          name="description"
          multiline
          numberOfLines={4}
          onChange={setValue}
          placeholder="Describe yourself in a few words..."
          value={values.description}
        />
      </Input>

      <Input label="Cancellation policy" error={errors.cancellation_policy}>
        <TextInput
          name="cancellation_policy"
          multiline
          numberOfLines={4}
          onChange={setValue}
          placeholder="Your cancellation policy..."
          value={values.cancellation_policy}
        />
      </Input>

      <Input label="Payment method" error={errors.payment_method}>
        <TextInput
          name="payment_method"
          multiline
          numberOfLines={4}
          onChange={setValue}
          placeholder="Your payment method..."
          value={values.payment_method}
        />
      </Input>

      <Button em onPress={handleSubmit} loading={isUpdating}>
        Save changes
      </Button>
    </Form>
  );
}
